/**
 * The frame an agent works in — its face plus its own route tree, inside the
 * pane.
 *
 * Home's detail pane hosts one screen per agent. That is enough for an agent
 * whose surface IS one screen, and wrong for every agent whose work has depth:
 * with nowhere to push, a board's "start this" had to leave the twin entirely
 * (see agentRoutes.tsx). This mounts the agent's declared screens as descendant
 * routes of `/home`, under `/home/a/<slug>/…`, so the depth is reachable with
 * the frame still around it.
 *
 * THE INNER PATH IS A REAL URL, not pane state. React Router allows exactly one
 * Router per app — a pane-local MemoryRouter throws "You cannot render a
 * <Router> inside another <Router>" — and the real URL is the better answer
 * anyway: reload, Back, and sharing a link all keep working, and the agent's
 * screens read their params from the router they already expect.
 *
 * An agent with no route module renders exactly as before, with no <Routes>
 * around it — declaring depth is what opts an agent in.
 */
import { type ReactNode } from 'react'
import { Route, Routes } from 'react-router-dom'

import { AGENT_FRAME_SEGMENT } from '@/lib/routes'
import { AgentBasePathProvider, FramedProvider } from './agentSurface'
import { AgentScreenFallback } from './AgentScreenFallback'
import { getAgentRoutes } from './agentRoutes'

export function AgentFrame({
  agentSlug,
  children,
}: {
  agentSlug: string | undefined
  children: ReactNode
}) {
  const declared = getAgentRoutes(agentSlug)
  const framedChildren = <FramedProvider value={true}>{children}</FramedProvider>

  if (!agentSlug || !declared || declared.routes.length === 0) return framedChildren

  return (
    <FramedProvider value={true}>
      <AgentBasePathProvider agentSlug={agentSlug}>
        {/* One boundary for the screens an agent lazy-loads — the same
            treatment AgentApp gives the full-screen surface, so a screen never
            needs to know which surface it is on. */}
        <AgentScreenFallback>
          {/* Descendant routes: relative to the /home/* match, so the agent's
              own `/runs/:id` becomes `/home/a/<slug>/runs/:id`. */}
          <Routes>
            {declared.routes.map((route) => (
              <Route
                key={route.path}
                path={`${AGENT_FRAME_SEGMENT}/${agentSlug}${route.path}`}
                element={route.element}
              />
            ))}
            {/* Anything else under /home — including /home itself — is the
                agent's face, which is what the pane was already rendering. */}
            <Route path="*" element={framedChildren} />
          </Routes>
        </AgentScreenFallback>
      </AgentBasePathProvider>
    </FramedProvider>
  )
}
